import type Phaser from 'phaser';
import { FRUIT_TIERS, PHYSICS } from '../config';
import type { MergeCandidate } from '../systems/mergeSystem';

const FRAME = { idle: 0, blink: 1, squish: 2, happy: 3 } as const;

export interface FruitDeps {
  scene: Phaser.Scene;
  tier: number;
  x: number;
  y: number;
  vx?: number;
  vy?: number;
  angle?: number;
  /** Texture-key prefix; full key is `${prefix}-${FRUIT_TIERS[tier].name}`. */
  texturePrefix: string;
}

export class Fruit implements MergeCandidate {
  readonly id: number;
  readonly tier: number;
  readonly sprite: Phaser.GameObjects.Sprite;
  readonly body: MatterJS.BodyType;
  merging = false;
  private blinkEvent: Phaser.Time.TimerEvent | null = null;
  private squishEvent: Phaser.Time.TimerEvent | null = null;
  private destroyed = false;
  private readonly deps: FruitDeps;

  constructor(deps: FruitDeps) {
    this.deps = deps;
    const tierDef = FRUIT_TIERS[deps.tier];
    if (!tierDef) throw new Error(`[Fruit] invalid tier ${deps.tier}`);
    this.tier = deps.tier;
    const textureKey = `${deps.texturePrefix}-${tierDef.name}`;
    // tier.radius is a CSS-px design value. With Supernapie HiDPI, world coords
    // are in physical px, so scale up by dpr — body and sprite both end up at
    // physical-px sizes that match the intended CSS-px visual diameter.
    const dpr = (deps.scene.registry.get('dpr') as number) ?? 1;
    const scaledRadius = tierDef.radius * dpr;

    this.body = deps.scene.matter.add.circle(deps.x, deps.y, scaledRadius, {
      friction: PHYSICS.friction,
      frictionAir: PHYSICS.frictionAir,
      restitution: PHYSICS.restitution,
      density: PHYSICS.density * (1 + deps.tier * 0.2),
      sleepThreshold: PHYSICS.sleepThreshold,
      label: `fruit-${tierDef.name}`,
    });
    this.id = this.body.id;
    if (deps.vx !== undefined || deps.vy !== undefined) {
      deps.scene.matter.body.setVelocity(this.body, { x: deps.vx ?? 0, y: deps.vy ?? 0 });
    }
    if (deps.angle !== undefined) {
      deps.scene.matter.body.setAngle(this.body, deps.angle);
    }

    this.sprite = deps.scene.add.sprite(deps.x, deps.y, textureKey, FRAME.idle);
    // Preserve the frame's natural aspect ratio so the fruit drawing inside
    // isn't squished. The fruit drawing occupies roughly VISUAL_FRACTION of
    // the frame's shorter dimension, so scale the sprite so that the visible
    // fruit comes out at ~physics diameter.
    const VISUAL_FRACTION = 0.78;
    const frame = this.sprite.frame;
    const frameW = frame.width;
    const frameH = frame.height;
    const shorter = Math.min(frameW, frameH);
    const desiredFruitDiameter = scaledRadius * 2;
    const scaleFactor = desiredFruitDiameter / (shorter * VISUAL_FRACTION);
    this.sprite.setDisplaySize(frameW * scaleFactor, frameH * scaleFactor);
    this.sprite.setDepth(10 + deps.tier);

    this.scheduleNextBlink();
  }

  get x(): number {
    return this.body.position.x;
  }
  get y(): number {
    return this.body.position.y;
  }
  get vx(): number {
    return this.body.velocity.x;
  }
  get vy(): number {
    return this.body.velocity.y;
  }

  syncSpriteToBody(): void {
    // No Math.round — sub-pixel motion is required for smooth interpolation.
    this.sprite.x = this.body.position.x;
    this.sprite.y = this.body.position.y;
    this.sprite.rotation = this.body.angle;
  }

  private scheduleNextBlink(): void {
    if (this.destroyed) return;
    const delay = 4000 + Math.random() * 3000;
    this.blinkEvent = this.deps.scene.time.delayedCall(delay, () => {
      this.blinkEvent = null;
      if (this.destroyed || this.merging) return;
      this.sprite.setFrame(FRAME.blink);
      this.blinkEvent = this.deps.scene.time.delayedCall(200, () => {
        this.blinkEvent = null;
        if (this.destroyed || this.merging) return;
        this.sprite.setFrame(FRAME.idle);
        this.scheduleNextBlink();
      });
    });
  }

  playSquish(): void {
    if (this.merging) return;
    if (this.squishEvent) return;
    this.sprite.setFrame(FRAME.squish);
    this.squishEvent = this.deps.scene.time.delayedCall(150, () => {
      this.squishEvent = null;
      if (this.merging) return;
      this.sprite.setFrame(FRAME.idle);
    });
  }

  playHappy(): void {
    this.sprite.setFrame(FRAME.happy);
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.blinkEvent?.remove(false);
    this.blinkEvent = null;
    this.squishEvent?.remove(false);
    this.squishEvent = null;
    this.deps.scene.matter.world.remove(this.body);
    this.sprite.destroy();
  }
}
